import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TextField, Button, CircularProgress } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

import type { IPartner, ICountry, ILanguage, IrMap, IrKvp } from "App/Modules/Models";
import type {
    IPartnersRepository,
    ITimeZonesRepository,
    ICountriesRepository,
    ILocalesRepository,
} from "App/Modules/Repositories";
import "./_locale-and-time.scss";

type LocaleAndTimeDialogProps = {
    onClose: () => void;
    partnersRepository: IPartnersRepository;
    partner: IPartner;
    timeZonesRepository: ITimeZonesRepository;
    countriesRepository: ICountriesRepository;
    localesRepository: ILocalesRepository;
    onLocaleAndTimeChange: () => void;
};

export const LocaleAndTimeDialog = (props: LocaleAndTimeDialogProps) => {
    const { onClose, partner, countriesRepository, localesRepository, partnersRepository, timeZonesRepository } = props;
    const { t } = useTranslation();
    const [saveError, setSaveError] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [isChangesSaved, setIsChangesSaved] = useState(false);

    const [countriesIsLoading, setCountriesIsLoading] = useState(true);
    const [allCountries, setAllCountries] = useState<ICountry[]>([]);
    const [selectedCountry, setSelectedCountry] = useState<ICountry>();

    const [localesIsLoading, setLocalesIsLoading] = useState(true);
    const [allLocales, setAllLocales] = useState<string[]>([]);
    const [selectedLocale, setSelectedLocale] = useState<string>();

    const [languagesIsLoading, setLanguagesIsLoading] = useState(true);
    const [allLanguages, setAllLanguages] = useState<ILanguage[]>([]);
    const [selectedLanguage, setSelectedLanguage] = useState<ILanguage>();

    const [timezonesIsLoading, setTimeZonesIsLoading] = useState(true);
    const [allTimeZones, setAllTimeZones] = useState<IrMap<string, string>>([]);
    const [selectedTimeZone, setSelectedTimeZone] = useState<IrKvp<string, string>>();

    const [selectedTimeInMinutes, setSelectedTimeInMinutes] = useState(partner.autoLogoutMinutes);

    const handleClose = () => {
        onClose();
    };

    const handleSave = (event: React.MouseEvent) => {
        event.preventDefault();
        const newPartnerValue = {} as IPartner;

        if (partner.defaultCountryId !== selectedCountry.id) {
            newPartnerValue.defaultCountryId = selectedCountry.id;
        }

        if (partner.defaultLocale !== selectedLocale) {
            newPartnerValue.defaultLocale = selectedLocale;
        }

        if (partner.defaultLanguageKey !== selectedLanguage.key) {
            newPartnerValue.defaultLanguageKey = selectedLanguage.key;
        }

        if (partner.defaultTimeZoneName !== selectedTimeZone.key) {
            newPartnerValue.defaultTimeZoneName = selectedTimeZone.key;
        }

        if (partner.autoLogoutMinutes !== selectedTimeInMinutes) {
            newPartnerValue.autoLogoutMinutes = selectedTimeInMinutes;
        }

        setIsButtonDisabled(true);
        setIsChangesSaved(true);
        partnersRepository.patch(partner.id, newPartnerValue).subscribe(
            (response) => {
                if (!response.hasError && response.data) {
                    props.onLocaleAndTimeChange();
                    onClose();
                }
            },
            () => {
                setSaveError(true);
            },
        );
    };

    // Returns a specific country based on country ID
    const getCountryById = (countryId: string, countries: ICountry[]) => {
        const returnCountry = countries.find((_country) => _country.id === countryId);
        return returnCountry;
    };

    // Returns default locale
    const getLocale = (locale: string, locales: string[]) => {
        const returnLocale = locales.find((_locale) => _locale === locale);
        return returnLocale;
    };

    // Returns default language by key
    const getLanguageByKey = (languageKey: string, languages: ILanguage[]) => {
        const returnLanguage = languages.find((lang) => lang.key === languageKey);
        return returnLanguage;
    };

    // Returns default timeZone by key
    const getTimeZoneByValue = (timeZoneKey: string, timeZones: IrMap<string, string>) => {
        const time = timeZones.find((timeZone) => timeZone.key === timeZoneKey);
        return time;
    };

    useEffect(() => {
        // Load countries
        countriesRepository.getAllCountries().subscribe(
            (response) => {
                if (!response.hasError) {
                    // Sort list and update states
                    setAllCountries(response.data.sort((a, b) => a.alpha2.localeCompare(b.alpha2)));
                    setCountriesIsLoading(false);
                    setSelectedCountry(getCountryById(partner.defaultCountryId, response.data));
                }
            },
            (error) => {
                console.log(error);
                setCountriesIsLoading(false);
            },
        );

        // Load locales
        localesRepository.getAllLocales().subscribe(
            (response) => {
                if (!response.hasError) {
                    // Sort list and update states
                    setAllLocales(response.data.sort((a, b) => a.localeCompare(b)));
                    setLocalesIsLoading(false);
                    setSelectedLocale(getLocale(partner.defaultLocale, response.data));
                }
            },
            (error) => {
                console.log(error);
                setLocalesIsLoading(false);
            },
        );

        // Load languages
        partnersRepository.getPartnerLanguages(partner.id).subscribe(
            (response) => {
                if (!response.hasError) {
                    // Sort list and update states
                    setAllLanguages(response.data.sort((a, b) => a.key.localeCompare(b.key)));
                    setLanguagesIsLoading(false);
                    setSelectedLanguage(getLanguageByKey(partner.defaultLanguageKey, response.data));
                }
            },
            (error) => {
                console.log(error);
                setLanguagesIsLoading(false);
            },
        );

        // Load Time zones
        timeZonesRepository.getAllTimeZones().subscribe(
            (response) => {
                if (!response.hasError) {
                    // Sort list and update states
                    setAllTimeZones(response.data.sort((a, b) => a.value.localeCompare(b.value)));
                    setTimeZonesIsLoading(false);
                    setSelectedTimeZone(getTimeZoneByValue(partner.defaultTimeZoneName, response.data));
                }
            },
            (error) => {
                console.log(error);
                setTimeZonesIsLoading(false);
            },
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="dialog card locale-and-time">
            <div className="dialog__section dialog__section--full">
                <h1 className="title title--h1">{t("Entities.Partner.Settings")}</h1>
            </div>
            <div className="dialog__section dialog__section--full">
                <form className="form">
                    <div className="form__row">
                        <p className="caption">{t("Entities.Partner.DefaultCountry")}</p>
                        <Autocomplete
                            options={allCountries}
                            getOptionLabel={(country) => t(`Entities.Country.${country.alpha2}`)}
                            value={selectedCountry || null}
                            onChange={(e, value: ICountry) => {
                                setSelectedCountry(value);
                                setIsButtonDisabled(false);
                            }}
                            loading={countriesIsLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    size="small"
                                    inputProps={{
                                        ...params.inputProps,
                                        autoComplete: "new-password", // disable autocomplete and autofill
                                    }}
                                />
                            )}
                            renderOption={(properties) => (
                                <div className="paragraph paragraph--no-spacing">
                                    <img
                                        className="paragraph__icon"
                                        loading="lazy"
                                        width="20"
                                        src={`https://flagcdn.com/w20/${properties.alpha2.toLowerCase()}.png`}
                                        srcSet={`https://flagcdn.com/w40/${properties.alpha2.toLowerCase()}.png 2x`}
                                        alt=""
                                    />
                                    {t(`Entities.Country.${properties.alpha2}`)}
                                </div>
                            )}
                        />
                    </div>
                    <div className="form__row">
                        <p className="caption">{t("Entities.Partner.DefaultLocale")}</p>
                        <Autocomplete
                            options={allLocales}
                            getOptionLabel={(locale) => t(`Entities.Locale.${locale}`)}
                            value={selectedLocale || null}
                            onChange={(e, value: string) => {
                                setSelectedLocale(value);
                                setIsButtonDisabled(false);
                            }}
                            loading={localesIsLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    size="small"
                                    inputProps={{
                                        ...params.inputProps,
                                        autoComplete: "new-password",
                                    }}
                                />
                            )}
                        />
                    </div>
                    <div className="form__row">
                        <p className="caption">{t("Entities.Partner.DefaultLanguage")}</p>
                        <Autocomplete
                            options={allLanguages}
                            getOptionLabel={(language) => t(`Entities.Language.${language.key}`)}
                            value={selectedLanguage || null}
                            onChange={(e, value: ILanguage) => {
                                setSelectedLanguage(value);
                                setIsButtonDisabled(false);
                            }}
                            loading={languagesIsLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    size="small"
                                    inputProps={{
                                        ...params.inputProps,
                                        autoComplete: "new-password",
                                    }}
                                />
                            )}
                        />
                    </div>
                    <div className="form__row">
                        <p className="caption">{t("Entities.Partner.DefaultTimeZone")}</p>
                        <Autocomplete
                            options={allTimeZones}
                            getOptionLabel={(timeZone) => t(`Entities.TimeZone.${timeZone.key}`)}
                            value={selectedTimeZone || null}
                            onChange={(e, value: any) => {
                                setSelectedTimeZone(value);
                                setIsButtonDisabled(false);
                            }}
                            loading={timezonesIsLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    size="small"
                                    inputProps={{
                                        ...params.inputProps,
                                        autoComplete: "new-password",
                                    }}
                                />
                            )}
                        />
                    </div>
                    <div className="form__row">
                        <p className="caption">{t("Entities.Partner.AutoLogoutMinutes")}</p>
                        <TextField
                            variant="outlined"
                            size="small"
                            type="number"
                            fullWidth
                            value={selectedTimeInMinutes}
                            onChange={(e) => {
                                setSelectedTimeInMinutes(e.target.value ? parseInt(e.target.value, 10) : 0);
                                setIsButtonDisabled(false);
                            }}
                        />
                    </div>
                </form>
            </div>
            {saveError ? (
                <div className="dialog__section dialog__section--full">
                    <div className="notice notice--critical">
                        <FontAwesomeIcon className="notice__icon" fixedWidth icon={faExclamationTriangle} />
                        <p className="caption">{t("Error.ErrorSaving")}</p>
                    </div>
                </div>
            ) : null}
            <div className="dialog__section--buttons">
                <Button onClick={handleClose}>{t("Gui.Buttons.Cancel")}</Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={isButtonDisabled}
                    startIcon={isChangesSaved ? <CircularProgress size={20} color="inherit" /> : null}
                    onClick={(e: React.MouseEvent) => handleSave(e)}
                >
                    {t("Gui.Buttons.Save")}
                </Button>
            </div>
        </div>
    );
};
